import { formatDateTime } from '@/components/BatchList';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { UserOrganizationRole } from '@/graphql/graphql';
import {
  organizationUsersQuery,
  removeUserFromOrganizationMutation,
  updateUserRoleInOrganizationMutation,
} from '@/queries/organization.query';
import { UserPlusSolid } from '@mynaui/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MoreHorizontal, Search } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UserInviteDialog } from './UserInviteDialog';

interface UsersListProps {
  organizationId: string;
}

const roleTranslationKey: Record<UserOrganizationRole, string> = {
  ADMIN: 'list.roleAdmin',
  EDITOR: 'list.roleEditor',
  MEMBER: 'list.roleMember',
  BILLING_MANAGER: 'list.roleBillingManager',
};

export function UsersList({ organizationId }: UsersListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const { t } = useTranslation('users');

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', organizationId],
    queryFn: () => execute(organizationUsersQuery),
  });

  const queryClient = useQueryClient();

  const removeUserMutation = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      return execute(removeUserFromOrganizationMutation, {
        organizationId,
        userId,
      });
    },
    onSuccess: () => {
      analytics.capture('User Removed From Organization');
      toast.success(t('list.userRemoved'));
      queryClient.invalidateQueries({ queryKey: ['users', organizationId] });
    },
    onError: (error: any) => {
      toast.error(error.message || t('list.failedRemove'));
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: UserOrganizationRole }) => {
      return execute(updateUserRoleInOrganizationMutation, {
        organizationId,
        userId,
        role,
      });
    },
    onSuccess: () => {
      analytics.capture('User Role Changed');
      toast.success(t('list.roleUpdated'));
      queryClient.invalidateQueries({ queryKey: ['users', organizationId] });
    },
    onError: (error: any) => {
      toast.error(error.message || t('list.failedUpdateRole'));
    },
  });

  const users = data?.users || [];
  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleRemoveUser = (userId: string, userName: string) => {
    if (window.confirm(t('list.confirmRemove', { name: userName }))) {
      removeUserMutation.mutate({ userId });
    }
  };

  const handleUpdateRole = (userId: string, newRole: UserOrganizationRole) => {
    updateRoleMutation.mutate({ userId, role: newRole });
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='flex flex-col items-center justify-center'>
          <Loader2 className='size-8 animate-spin' />
          <span className='font-semibold pt-4 text-slate-700 dark:text-white'>{t('list.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <p className='text-red-600 dark:text-red-400'>{t('list.errorLoading', { error: error.message })}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <div className='relative flex-1 max-w-sm'>
          <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
          <Input
            placeholder={t('list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='pl-8 bg-white'
          />
        </div>
        <Button onClick={() => setInviteDialogOpen(true)}>
          <UserPlusSolid className='size-4' />
          {t('list.inviteUsers')}
        </Button>
      </div>
      <UserInviteDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        organizationId={organizationId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['invitations', organizationId] });
        }}
      />
      <div className='rounded-md border bg-white dark:bg-stone-900 dark:border-stone-800'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('list.user')}</TableHead>
              <TableHead>{t('list.email')}</TableHead>
              <TableHead>{t('list.role')}</TableHead>
              <TableHead>{t('list.joined')}</TableHead>
              <TableHead className='w-[50px]'></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className='text-center py-8 text-muted-foreground'>
                  {searchTerm ? t('list.noUsersSearch') : t('list.noUsers')}
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className='font-medium'>
                    <div className='flex items-center space-x-3'>
                      <div className='h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center'>
                        <img
                          src={user.avatar?.url || getPlaceholderAvatar(user?.email || user?.id || '')}
                          alt={user.name}
                          className='h-8 w-8 rounded-full object-cover'
                        />
                      </div>
                      <div>
                        <div className='font-medium'>{user.name}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        user.role === 'ADMIN'
                          ? 'bg-amber-100 dark:bg-rose-900 text-amber-800 dark:text-rose-100'
                          : user.role === 'EDITOR'
                            ? 'bg-blue-100 dark:bg-blue-700 text-blue-800 dark:text-blue-100'
                            : user.role === 'BILLING_MANAGER'
                              ? 'bg-violet-100 dark:bg-violet-700 text-violet-800 dark:text-violet-100'
                              : 'bg-green-100 dark:bg-green-700 text-green-800 dark:text-green-100'
                      }`}
                    >
                      {t(roleTranslationKey[user.role || 'MEMBER'])}
                    </span>
                  </TableCell>
                  <TableCell>{formatDateTime(user.joinedAt!)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant='ghost' className='h-8 w-8 p-0'>
                          <span className='sr-only'>{t('list.openMenu')}</span>
                          <MoreHorizontal className='h-4 w-4' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        <DropdownMenuLabel>{t('list.actions')}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleUpdateRole(user.id, 'MEMBER')}
                          disabled={user.role === 'MEMBER' || updateRoleMutation.isPending}
                        >
                          {t('list.makeMember')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleUpdateRole(user.id, 'EDITOR')}
                          disabled={user.role === 'EDITOR' || updateRoleMutation.isPending}
                        >
                          {t('list.makeEditor')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleUpdateRole(user.id, 'ADMIN')}
                          disabled={user.role === 'ADMIN' || updateRoleMutation.isPending}
                        >
                          {t('list.makeAdmin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleUpdateRole(user.id, 'BILLING_MANAGER')}
                          disabled={user.role === 'BILLING_MANAGER' || updateRoleMutation.isPending}
                        >
                          {t('list.makeBillingManager')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleRemoveUser(user.id, user.name)}
                          disabled={removeUserMutation.isPending}
                          className='text-red-600'
                        >
                          {t('list.removeFromOrg')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
